import { forwardRef, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { nodeToPng, shareOrDownload } from "@/lib/share";
import { bumpStreak } from "@/lib/device";
import { haptic } from "@/lib/haptics";

const FORMATS = {
  story: { w: 1080, h: 1920, label: "9:16" },
  square: { w: 1080, h: 1080, label: "1:1" },
};
const STYLES = { editorial: { label: "Editorial" }, frost: { label: "Frost" } };

const THEME = {
  editorial: { bg: "#f4f1ea", ink: "#1a1a1a", soft: "#5c5c5c", line: "#e2dfd8", chip: "rgba(0,0,0,0.04)" },
  frost: { bg: "linear-gradient(180deg,#f2f8f9 0%,#dbe8ea 55%,#c6d6d9 100%)", ink: "#16242a", soft: "#5a7176", line: "rgba(255,255,255,0.9)", chip: "rgba(255,255,255,0.55)" },
};

const RecipeCard = forwardRef(({ recipe, fmt, style }, ref) => {
  const isStory = fmt.h > 1200;
  const t = THEME[style] || THEME.frost;
  const steps = (recipe.steps || []).slice(0, isStory ? 6 : 4);
  return (
    <div ref={ref} style={{ width: fmt.w, height: fmt.h, background: t.bg, color: t.ink, padding: isStory ? "110px 90px" : "80px", display: "flex", flexDirection: "column", boxSizing: "border-box", fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div style={{ fontSize: 30, letterSpacing: 8, textTransform: "uppercase", fontWeight: 600, color: t.soft }}>From the fridge</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: isStory ? 104 : 78, lineHeight: 0.96, letterSpacing: -3, marginTop: 34 }}>{recipe.title}</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: isStory ? 44 : 36, color: t.soft, marginTop: 22, lineHeight: 1.25 }}>{recipe.quip}</div>
      {recipe.time && <div style={{ fontSize: 30, color: t.soft, marginTop: 20, fontWeight: 500 }}>Ready in {recipe.time}</div>}
      <div style={{ height: 2, background: t.line, margin: isStory ? "48px 0" : "36px 0" }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: isStory ? 20 : 14 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 22, alignItems: "flex-start" }}>
            <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 999, background: t.ink, color: t.bg.startsWith("#") ? t.bg : "#eef5f6", fontSize: 26, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
            <div style={{ fontSize: isStory ? 34 : 28, lineHeight: 1.3 }}>{s}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 40 }}>
        <div style={{ width: 34, height: 34, borderRadius: 999, border: `3px solid ${t.ink}` }} />
        <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: 1 }}>What's in the Fridge</div>
      </div>
    </div>
  );
});
RecipeCard.displayName = "RecipeCard";

export default function RecipeShareModal({ recipe, onClose }) {
  const [format, setFormat] = useState("story");
  const [style, setStyle] = useState("frost");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const fmt = FORMATS[format];
  const scale = 300 / fmt.w;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await nodeToPng(cardRef.current);
      await shareOrDownload(dataUrl, `recipe-${style}-${format}.png`, `${recipe.title} — from What's in the Fridge.`);
      const n = bumpStreak();
      haptic([12, 40, 12]);
      toast(`Recipe shared. ${n} card${n > 1 ? "s" : ""} out there.`);
      onClose();
    } catch {
      toast("Couldn't plate that up — give it another go.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="recipe-share-modal">
      <motion.div className="w-full max-w-md bg-[#eef5f6]/95 backdrop-blur rounded-t-2xl sm:rounded-lg p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] max-h-[94dvh] overflow-y-auto" initial={{ y: 40 }} animate={{ y: 0 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">Share this recipe</h3>
          <button data-testid="recipe-share-close-btn" onClick={onClose} className="w-9 h-9 rounded-full glass flex items-center justify-center">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex justify-center">
          <div style={{ width: 300, height: fmt.h * scale }} className="overflow-hidden rounded-sm hairline shadow-[0_10px_30px_rgba(20,40,45,0.18)]">
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <RecipeCard ref={cardRef} recipe={recipe} fmt={fmt} style={style} />
            </div>
          </div>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mt-6 mb-2">Style</p>
        <div className="flex gap-2">
          {Object.entries(STYLES).map(([k, s]) => (
            <button key={k} data-testid={`recipe-style-${k}-btn`} onClick={() => setStyle(k)} className={`flex-1 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${style === k ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}>{s.label}</button>
          ))}
        </div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mt-4 mb-2">Format</p>
        <div className="flex gap-2">
          {Object.entries(FORMATS).map(([k, f]) => (
            <button key={k} data-testid={`recipe-format-${k}-btn`} onClick={() => setFormat(k)} className={`flex-1 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${format === k ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}>{f.label}</button>
          ))}
        </div>
        <button data-testid="recipe-share-generate-btn" onClick={handleShare} disabled={busy} className="btn-ink w-full rounded-sm py-4 mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 spin-slow" /> : <Share2 className="w-5 h-5" strokeWidth={1.5} />}
          {busy ? "Plating up…" : "Share the recipe"}
        </button>
      </motion.div>
    </motion.div>
  );
}
